/**
 * Per-tour settings persisted on the tour Firestore doc itself, accessed
 * through the tourCollaborators GCF (so we don't need v1 monolith changes
 * or to widen Firestore rules for direct client reads).
 *
 * Currently: start location (which pano / which floorplan to open the tour
 * on by default). Authorization mirrors updateTourInfo — owner /
 * collaborator / admin can mutate.
 */

import { callCollaboratorsApi } from './collaboratorsApi';

/** Minimal shape needed to rebuild iframe params + picker preview. */
export interface PersistedStartLocation {
  /**
   * Legacy structured payload — the picker in the dashboard writes this
   * shape. When `savedUrl` is set below, `type` may be absent and the
   * dashboard uses the URL verbatim instead of rebuilding params.
   */
  type?: 'pano' | 'floorplan';
  panoId?: string;
  folderPath?: string;
  roomName?: string;
  floorName?: string;
  thumbnailUrl?: string;
  fit?: 'cover' | 'contain';
  url?: string;
  /**
   * Full viewer URL captured by the viewer's "Set Start Location" mode
   * (window.location.href at Save-click). When present, iframe src +
   * share links use this verbatim — pitch, yaw, mode, floor, pano are
   * all round-tripped by the URL itself. `buildTourLink` still layers
   * dashboard-runtime params (layer, hideLayers, noTrack, flythroughEdit)
   * on top via URLSearchParams.set so they override any embedded values.
   */
  savedUrl?: string;
}

interface GetStartLocationResult {
  startLocation: PersistedStartLocation | null;
}

export async function getTourStartLocation(
  tourId: string,
): Promise<PersistedStartLocation | null> {
  const result = await callCollaboratorsApi<GetStartLocationResult>({
    action: 'getTourStartLocation',
    tourId,
  });
  return result?.startLocation ?? null;
}

export async function setTourStartLocation(
  tourId: string,
  startLocation: PersistedStartLocation | null,
): Promise<void> {
  await callCollaboratorsApi<{ success: boolean }>({
    action: 'setTourStartLocation',
    tourId,
    startLocation,
  });
}

interface GetDashboardDefaultLayerResult {
  defaultLayer: string | null;
}

/**
 * Default viewer layer applied to the dashboard's iframe and outgoing
 * Share URLs. Stored on the tour Firestore doc as `dashboardDefaultLayer`
 * — the viewer doesn't read this field, so the setting is dashboard-only
 * (same pattern as the start-location persistence).
 */
export async function getTourDashboardDefaultLayer(
  tourId: string,
): Promise<string | null> {
  const result = await callCollaboratorsApi<GetDashboardDefaultLayerResult>({
    action: 'getTourDashboardDefaultLayer',
    tourId,
  });
  return result?.defaultLayer ?? null;
}

export async function setTourDashboardDefaultLayer(
  tourId: string,
  defaultLayer: string | null,
): Promise<void> {
  await callCollaboratorsApi<{ success: boolean }>({
    action: 'setTourDashboardDefaultLayer',
    tourId,
    defaultLayer,
  });
}
